// Inspecteur detail drill-ins (design-vs-build Batch 2): Bestandssysteem / Bewijscontainer / Regulation.
// The registers were list-only; rows are now clickable into a detail page showing the entity + its linked
// context. Backend: GET /{bestandssystemen|bewijscontainers|regulations}/:id/detail. RLS-scoped.
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft } from "lucide-react";
import { DetailShell, DetailFields, DetailPanel, dv, type DRow } from "./_detail";

function BackLink({ to, label }: { to: "/inspector/filing-systems" | "/bewijscontainers" | "/inspector/regulations"; label: string }) {
  return <Link to={to} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="size-4" /> {label}</Link>;
}

export function FilingSystemDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/inspector/filing-systems/$id" });
  const q = useQuery({ queryKey: ["fs-detail", id], queryFn: () => api.getBestandssysteemDetail(id) as Promise<{ filingSystem: DRow; verwerkingen: DRow[] }> });
  const fs = (q.data as { filingSystem?: DRow } | undefined)?.filingSystem;
  return (
    <DetailShell back={<BackLink to="/inspector/filing-systems" label={t("insp.detail.backFiling")} />} title={dv(fs?.name)} q={q as never}>
      {(d) => { const f = d.filingSystem as DRow; return (<>
        <DetailFields items={[[t("risk.f.category"), f.category], [t("risk.f.description"), f.description]]} />
        <DetailPanel title={t("insp.detail.fs.verwerkingen")} rows={(d.verwerkingen as DRow[]) ?? []} empty={t("insp.detail.fs.noVerwerkingen")}
          renderRow={(r) => (<>
            <Link to="/verwerkingen/$id" params={{ id: String(r.id) }} className="font-medium text-primary hover:underline">{dv(r.name)}</Link>
            <Badge variant="secondary">{dv(r.status)}</Badge>
          </>)} />
      </>); }}
    </DetailShell>
  );
}

export function BewijscontainerDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/bewijscontainers/$id" });
  const q = useQuery({ queryKey: ["bc-detail", id], queryFn: () => api.getBewijscontainerDetail(id) as Promise<{ container: DRow; files: DRow[]; links: DRow[] }> });
  const c = (q.data as { container?: DRow } | undefined)?.container;
  const fmtSize = (b: unknown) => { const n = Number(b); return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n >= 1024 ? `${(n / 1024).toFixed(0)} KB` : `${n} B`; };
  return (
    <DetailShell back={<BackLink to="/bewijscontainers" label={t("insp.detail.backEvidence")} />} title={dv(c?.name)} q={q as never}>
      {(d) => { const x = d.container as DRow; return (<>
        <DetailFields items={[[t("risk.f.description"), x.description], [t("insp.f.anchorKind"), x.anchorKind], [t("insp.f.anchorId"), x.anchorId]]} />
        <DetailPanel title={t("insp.detail.ev.files")} rows={(d.files as DRow[]) ?? []} empty={t("insp.detail.ev.noFiles")}
          renderRow={(r) => (<>
            <span className="min-w-0 truncate font-medium">{dv(r.fileName)}</span>
            <span className="flex shrink-0 items-center gap-2"><Badge variant="secondary">{dv(r.evidenceKind)}</Badge><span className="text-xs text-muted-foreground">{fmtSize(r.sizeBytes)}</span></span>
          </>)} />
        <DetailPanel title={t("insp.detail.ev.activities")} rows={(d.links as DRow[]) ?? []} empty={t("insp.detail.ev.noActivities")}
          renderRow={(r) => (<>
            <span className="min-w-0 truncate">{dv(r.activityName ?? r.targetKind)}</span>
            <Badge variant="outline">{dv(r.linkRole)}</Badge>
          </>)} />
      </>); }}
    </DetailShell>
  );
}

export function RegulationDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/inspector/regulations/$id" });
  const q = useQuery({ queryKey: ["reg-detail", id], queryFn: () => api.getRegulationDetail(id) as Promise<{ regulation: DRow; translations: DRow[]; enablement: DRow | null }> });
  const data = q.data as { regulation?: DRow; enablement?: DRow | null } | undefined;
  const reg = data?.regulation;
  return (
    <DetailShell back={<BackLink to="/inspector/regulations" label={t("insp.detail.backRegs")} />} title={[dv(reg?.shortCode), dv(reg?.title)].join(" · ")} q={q as never}>
      {(d) => { const r = d.regulation as DRow; const en = (d.enablement as DRow | null); return (<>
        <DetailFields items={[[t("insp.f.shortCode"), r.shortCode], [t("insp.f.source"), r.source], [t("insp.f.language"), r.canonicalLanguage], [t("insp.f.url"), r.publicationUrl], [t("insp.detail.reg.status"), en?.enabled ? t("insp.detail.reg.enabled") : t("insp.detail.reg.disabled")]]} />
        <DetailPanel title={t("insp.detail.reg.translations")} rows={(d.translations as DRow[]) ?? []} empty={t("insp.detail.reg.noTranslations")}
          renderRow={(r2) => (<>
            <span className="min-w-0 truncate font-medium">{dv(r2.title)}</span>
            <Badge variant="secondary" className="uppercase">{dv(r2.language)}</Badge>
          </>)} />
      </>); }}
    </DetailShell>
  );
}
